import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  DataTableRowData,
  Highlight,
  useSidePanel,
  Box as Wrapper,
} from '@node-space/ui-components'
import { TransactionStatusHighlight } from 'pages/Channels/components/TransactionStatusHighlight'
import { PaymentsResponse, PaymentType } from 'types/payments'
import { titleCase } from 'utils/utils'
import { CryptoPaymentsSidePanel } from '../SidePanel/CryptoPaymentsSidePanel'

interface PaymentTableProps {
  transactions: PaymentsResponse[]
  isLoading: boolean
}

const PaymentTable = ({ transactions }: PaymentTableProps) => {
  const { t } = useTranslation()

  const {
    isSidePanelOpen: isPaymentDetailsOpen,
    sidePanelData: transactionDetails,
    paging: pagingDetails,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(transactions)

  const tableColumns: DataTableHeaderProps[] = [
    {
      key: 'reference',
      label: t('payments.reference'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'amount',
      label: t('payments.amount'),
      variant: DataTableCellVariants.TEXT,
      align: DataTableColumnAlignments.RIGHT,
    },
    {
      key: 'date',
      label: t('payments.date'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'status',
      label: t('payments.status'),
      variant: DataTableCellVariants.TEXT,
    },
  ]

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!transactions?.length) return []

    return transactions?.map((transaction, index) => {
      const isPaymentIn = transaction?.type === PaymentType.IN
      return {
        id: transaction?.reference,
        testId: transaction?.reference,
        onRowClick: () => {
          setIsSidePanelOpen(true)
          updateSidePanel(index)
        },
        cells: [
          {
            key: 'reference',
            variant: DataTableCellVariants.TEXT,
            Text: transaction?.reference,
          },
          {
            key: 'amount',
            variant: DataTableCellVariants.TEXT,
            align: DataTableColumnAlignments.RIGHT,
            Text: isPaymentIn
              ? `+ ${transaction?.displayCurrency?.amount} ${transaction?.displayCurrency?.currency}`
              : `- ${transaction?.displayCurrency?.amount} ${transaction?.displayCurrency?.currency}`,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: `${dayjs(transaction?.dateCreated).format('DD MMM YYYY')}`,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: `${dayjs(transaction?.dateCreated).format('DD MMM YYYY')}`,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Text: titleCase(transaction?.status),
            Highlights: [
              <TransactionStatusHighlight
                key={`${transaction?.status}-${transaction?.reference}`}
                status={transaction?.status}
              />,
              ...(transaction?.latePayment
                ? [
                    <Highlight
                      key={`latePayment-${transaction?.reference}`}
                      type="neutral"
                    >{`${t('latePayment')}`}</Highlight>,
                  ]
                : []),
              ...(transaction?.overPayment
                ? [
                    <Highlight
                      key={`overPayment-${transaction?.reference}`}
                      type="neutral"
                    >{`${t('overPayment')}`}</Highlight>,
                  ]
                : []),
              ...(transaction?.hasEnhancedChecks
                ? [
                    <Highlight
                      key={`statusOnHold-${transaction?.reference}`}
                      type="warning"
                      variant="emphasis"
                    >{`${t('payments.statusOnHold')}`}</Highlight>,
                  ]
                : []),
            ],
          },
        ],
      }
    })
  }, [transactions])

  return (
    <Wrapper paddingT={24}>
      <DataTable headers={tableColumns} data={tableData} />
      <CryptoPaymentsSidePanel
        isOpen={isPaymentDetailsOpen}
        onClose={() => setIsSidePanelOpen(false)}
        cryptoPaymentDetails={transactionDetails}
        paging={pagingDetails}
      />
    </Wrapper>
  )
}

export default React.memo(PaymentTable)
